import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "@shared/api/baseQueryWithReauth";
import { configApi } from "@shared/lib/configApi";
import { normalizeHistoryFullReportData } from "../lib/normalize-full-report";
import {
    CreateFullReportData,
    CreateFullReportResponse,
    CreateReportOrderBody,
    CreateReportOrderResponse,
    HistoryFullReportData,
    HistoryFullReportResponse,
    HistoryUserReportsResponse,
    RecentReportByCode,
    RecentReportByCodeResponse,
    ReportOrderStatus,
    ReportOrderStatusResponse,
    UserReport,
    UserReportBalanceResponse
} from "./types";

export const historyApi = createApi({
    reducerPath: "historyApi",
    tagTypes: ["REPORTS_TAG"],
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        createFullReport: builder.mutation<CreateFullReportData, { code: string }>({
            query: ({ code }) => ({
                url: `${configApi.API_URL}/v1/reports/full`,
                method: "POST",
                body: { code }
            }),
            transformResponse: (response: CreateFullReportResponse) => response.data
        }),
        getUserReports: builder.query<UserReport[], void>({
            query: () => ({
                url: `${configApi.API_URL}/v1/reports/me`
            }),
            transformResponse: (response: HistoryUserReportsResponse) => {
                const seen = new Set<string>();
                return response.data.filter((report) => {
                    if (seen.has(report.vin)) return false;
                    seen.add(report.vin);
                    return true;
                });
            },
            providesTags: ["REPORTS_TAG"]
        }),
        getUserReportById: builder.query<HistoryFullReportData, string>({
            query: (reportId) => ({
                url: `${configApi.API_URL}/v1/reports/me/${reportId}`
            }),
            transformResponse: (response: HistoryFullReportResponse) =>
                normalizeHistoryFullReportData(response.data)
        }),
        createReportOrder: builder.mutation<CreateReportOrderResponse, CreateReportOrderBody>({
            query: (body) => ({
                url: `${configApi.API_URL}/v1/reports/orders`,
                method: "POST",
                body
            }),
            transformResponse: (response: CreateReportOrderResponse) => response
        }),
        getReportOrderStatus: builder.query<ReportOrderStatus, string>({
            query: (orderId) => ({
                url: `${configApi.API_URL}/v1/reports/orders/${orderId}`
            }),
            transformResponse: (response: ReportOrderStatusResponse) => response.data
        }),
        getUserReportBalance: builder.query<number, void>({
            query: () => `${configApi.API_URL}/v1/reports/users/me/balance`,
            transformResponse: (response: UserReportBalanceResponse) => response.data.amount
        }),
        getRecentReportByCode: builder.query<RecentReportByCode, string>({
            query: (code) => `${configApi.API_URL}/v1/reports/me/recent/${code}`,
            transformResponse: (response: RecentReportByCodeResponse) => response.data
        })
    })
});

export const {
    useCreateFullReportMutation,
    useGetUserReportsQuery,
    useGetUserReportByIdQuery,
    useCreateReportOrderMutation,
    useGetReportOrderStatusQuery,
    useGetUserReportBalanceQuery,
    useGetRecentReportByCodeQuery
} = historyApi;